import { z } from 'zod';
import { PersonView } from './people';
import { ChatGift, ChatMoney } from './money';
import { ChatInvite } from './visits';
import { StreakView } from './streaks';

// Chat (Lagos Life's chat, Sharon's posts of 8 October, and a look at it on 10 October; Jerry: everything,
// with moderation and rate limits from the start). A place's chat ("say something out loud", everyone at the
// place, kept for a day), direct messages (a stranger's first message waits under Message requests), and
// groups. Delivery is live over a WebSocket (realtime.ts); the HTTP routes are the record.

export const CHAT_TEXT_MAX = 500;
export const CHAT_PAGE = 50;
// a place's chat is kept this long
export const PLACE_CHAT_HOURS = 24;
// you may edit or delete your own message for everyone within this time
export const CHAT_EDIT_MINUTES = 15;
// rate limits: one message every 1.5 seconds, 30 a minute
export const CHAT_MIN_GAP_MS = 1500;
export const CHAT_PER_MINUTE = 30;

// the filters: scams, illegal sellers, fake "team" accounts and "free credit" offers. A message that matches
// is refused with a plain reason and recorded; the player is not told which words matched.
export const CHAT_FILTERS: { reason: string; test: RegExp }[] = [
  {
    reason: 'offers of free money or credit',
    test: /\b(free|bonus)\s+(money|credit|coins|cash|£|pounds)\b|\bgiveaway\b.*\b(send|dm)\b/i,
  },
  {
    reason: 'pretending to be the London Life team',
    test: /\b(london\s*life|ll)\s*(team|admin|support|staff|mod(erator)?)\b|\bofficial\s+(team|support)\b/i,
  },
  {
    reason: 'asking for money or payment details',
    test: /\b(send|transfer|pay)\s+(me\s+)?(money|cash|£\s?\d|\d+\s?(pounds|quid))\b|\b(bank|card)\s+(details|number)\b|\bpaypal\.me\b|\bcash\s?app\b/i,
  },
  {
    reason: 'selling drugs, weapons or stolen goods',
    test: /\b(weed|coke|cocaine|pills|mdma|ket|spice|xanax)\b.{0,30}\b(sell|buy|for sale|dm)\b|\b(sell|buy|for sale)\b.{0,30}\b(weed|coke|cocaine|pills|mdma|ket|spice|xanax|guns?|knives|shanks?)\b/i,
  },
  {
    reason: 'moving the conversation off the game for a deal',
    test: /\b(whats?app|telegram|signal)\b.{0,20}\b(\+?\d[\d\s-]{8,})\b|\b(\+?44|0)7\d{2}[\s-]?\d{3}[\s-]?\d{4}\b/i,
  },
  {
    reason: 'crypto or investment schemes',
    test: /\b(bitcoin|btc|crypto|forex|binary\s+options|double\s+your)\b.{0,30}\b(invest|profit|guarantee|return)/i,
  },
];
export function chatProblem(text: string): string | null {
  for (const filter of CHAT_FILTERS)
    if (filter.test.test(text)) return `That message was not sent: it looks like ${filter.reason}.`;
  return null;
}

// ---- reactions, stickers, groups, forwarding (Jerry, 10 October: everything from Sharon's list before launch)

// one reaction per person per message, from this set
export const CHAT_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'] as const;
export type ChatReactionEmoji = (typeof CHAT_REACTIONS)[number];
// our own London sticker pack (drawings in apps/web/public/stickers/<id>.svg)
export const STICKERS = {
  cuppa: 'Fancy a cuppa?',
  cheers_mate: 'Cheers mate',
  innit: 'Innit',
  bus: 'On the bus',
  black_cab: 'Taxi!',
  umbrella: 'Typical weather',
  rain_cloud: 'Raining again',
  pigeon: 'Pigeon',
  big_ben: 'Big Ben',
  fish_chips: 'Chippy tea',
  full_english: 'Full English',
  pint: 'Pint?',
  queue: 'Proper queue',
  sorted: 'Sorted',
  gutted: 'Gutted',
  bless: 'Bless',
  proper: 'Proper',
  oi_oi: 'Oi oi!',
  mind_out: 'Mind out',
  red_phone: 'Call me',
  crown: 'Royalty',
  fox: 'Urban fox',
  sunshine: 'Sun’s out!',
  night_bus: 'Night bus home',
} as const;
export type StickerId = keyof typeof STICKERS;
export const STICKER_IDS = Object.keys(STICKERS) as StickerId[];

// the tray's tabs (Lagos Life groups theirs as Naija / Hype / Love / Wahala)
export const STICKER_GROUPS: { label: string; stickers: StickerId[] }[] = [
  { label: 'Slang', stickers: ['innit', 'sorted', 'proper', 'bless', 'gutted', 'oi_oi', 'mind_out', 'cheers_mate'] },
  {
    label: 'London',
    stickers: ['big_ben', 'black_cab', 'bus', 'night_bus', 'red_phone', 'crown', 'queue', 'pigeon', 'fox'],
  },
  { label: 'Grub', stickers: ['cuppa', 'pint', 'fish_chips', 'full_english'] },
  { label: 'Weather', stickers: ['sunshine', 'umbrella', 'rain_cloud'] },
];

// one tap to say something common (Lagos Life: "How far? 👋🏾", "I dey o 😄"); ours in London English
export const QUICK_REPLIES = [
  'You alright? 👋',
  'Wagwan 😎',
  'Where you at?',
  'On my way 🚶',
  'Come round mine 🏠',
  'LOL 😂',
  'Cheers! 🙏',
  'Speak later ✌️',
] as const;

// a social action done to someone also shows in your chat with them ("You threw shade 😏 · 15:52")
export const SOCIAL_LINES: Record<string, { did: string; icon: string }> = {
  say_hello: { did: 'said hello', icon: '👋' },
  gist: { did: 'had a gist', icon: '🗣️' },
  crack_joke: { did: 'cracked a joke', icon: '😂' },
  throw_shade: { did: 'threw shade', icon: '😏' },
};
export const socialLine = (kind: string, who: string) => {
  const line = SOCIAL_LINES[kind];
  return line ? `${who} ${line.did} ${line.icon}` : `${who} did something`;
};

// voice notes (Lagos Life: hold to talk, or tap for hands-free; only from people you allow)
export const VOICE_MAX_SECONDS = 60;
export const VOICE_MIN_MS = 500;
// kept this long, then removed by the bucket's lifecycle rule (reported ones are copied aside and kept)
export const VOICE_KEEP_DAYS = 90;

export const GROUP_MAX_MEMBERS = 50;
// a group's picture is one of these
export const GROUP_ICONS = [
  '👥',
  '🍻',
  '⚽',
  '🎉',
  '🎮',
  '🎤',
  '🍔',
  '☕',
  '💼',
  '📚',
  '🏋️',
  '🎬',
  '🛍️',
  '🌳',
  '👑',
  '🦊',
] as const;
export type GroupIcon = (typeof GROUP_ICONS)[number];
const GroupIconSchema = z.enum(GROUP_ICONS as unknown as [GroupIcon, ...GroupIcon[]]);
export const GROUP_NAME_MAX = 40;
// forwarding: to this many chats at once
export const FORWARD_MAX = 5;

// ---- the API

export const ChatKind = z.enum(['place', 'direct', 'group']);
export type ChatKind = z.infer<typeof ChatKind>;
// system: what happened in a group; social: a social action between two people (text = the action's key)
// money: money sent, or a request for money (the card's details are in `money`); gift: a meal bought for them;
// invite: come over to my home; shop: visit my shop (a player's shop shared in a chat)
export const MessageKind = z.enum(['text', 'sticker', 'system', 'social', 'voice', 'money', 'gift', 'invite', 'shop']);
// "🛍️ Visit my shop": which shop, and whether it is open to players now
export const ChatShop = z.object({
  businessId: z.string().uuid(),
  name: z.string(),
  kind: z.string(),
  area: z.string(),
  open: z.boolean(),
});
export type ChatShop = z.infer<typeof ChatShop>;
export const ShareShopRequest = z.object({ businessId: z.string().uuid() });
export type ShareShopRequest = z.infer<typeof ShareShopRequest>;
// lines that are events, not things someone wrote: no menu, no reactions, never forwarded
export const isChatEvent = (kind: string) => kind === 'system' || kind === 'social';
export type MessageKind = z.infer<typeof MessageKind>;
export const ChatSender = PersonView.pick({ lifeId: true, firstName: true, username: true, look: true }).extend({
  userId: z.string().uuid(),
});
export type ChatSender = z.infer<typeof ChatSender>;
export const ChatReaction = z.object({ emoji: z.string(), userIds: z.array(z.string().uuid()) });
export type ChatReaction = z.infer<typeof ChatReaction>;
export const ChatMessage = z.object({
  id: z.string().uuid(),
  conversationId: z.string().uuid(),
  kind: MessageKind,
  sender: ChatSender,
  mine: z.boolean(),
  // the text, the sticker's id, or what happened (system); null when deleted for everyone
  text: z.string().nullable(),
  replyTo: z.object({ id: z.string().uuid(), firstName: z.string(), text: z.string().nullable() }).nullable(),
  forwarded: z.boolean(),
  // a voice note: how long it is, and the address that plays it (null once it has expired)
  voice: z.object({ durationMs: z.number().int(), playPath: z.string().nullable() }).nullable(),
  // money sent or asked for
  money: ChatMoney.nullish(),
  // a meal bought for someone
  gift: ChatGift.nullish(),
  invite: ChatInvite.nullish(),
  shop: ChatShop.nullish(),
  reactions: z.array(ChatReaction),
  editedAt: z.string().nullable(),
  deletedAt: z.string().nullable(),
  createdAt: z.string(),
  // direct messages only: the other person has read up to here (when they allow read receipts)
  seen: z.boolean(),
});
export type ChatMessage = z.infer<typeof ChatMessage>;
export const GroupMember = ChatSender.extend({ role: z.enum(['admin', 'member']) });
export type GroupMember = z.infer<typeof GroupMember>;
export const Conversation = z.object({
  id: z.string().uuid(),
  kind: ChatKind,
  // the place, the other person, or the group
  placeId: z.string().nullable(),
  other: ChatSender.nullable(),
  group: z
    .object({
      name: z.string(),
      icon: z.string(),
      members: z.array(GroupMember),
      myRole: z.enum(['admin', 'member']),
    })
    .nullable(),
  // a direct chat from someone you have not chatted with: it waits under Message requests (no badge) until
  // you reply
  request: z.boolean(),
  // the other person was in the game in the last few minutes
  online: z.boolean(),
  // voice notes here: can I send one (direct chats: only when they allow me; groups: yes), and do I allow theirs
  voice: z.object({ canSend: z.boolean(), iAllow: z.boolean() }).nullable(),
  unread: z.number().int(),
  lastMessage: ChatMessage.nullable(),
  // my read receipts setting
  readReceipts: z.boolean(),
  // a direct chat's streak (Lagos Life's 🔥 n)
  streak: StreakView.nullish(),
});
export type Conversation = z.infer<typeof Conversation>;

export const ChatOpen = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('place'), placeId: z.string().min(1).max(40) }).strict(),
  z.object({ kind: z.literal('direct'), lifeId: z.string().uuid() }).strict(),
  // "Message someone: @username"
  z
    .object({
      kind: z.literal('username'),
      username: z
        .string()
        .trim()
        .transform((value) => value.replace(/^@/, ''))
        .pipe(z.string().min(3).max(20)),
    })
    .strict(),
  z.object({ kind: z.literal('group'), conversationId: z.string().uuid() }).strict(),
]);
export type ChatOpen = z.infer<typeof ChatOpen>;
export const ChatOpenReply = z.object({ conversation: Conversation, messages: z.array(ChatMessage) });
export type ChatOpenReply = z.infer<typeof ChatOpenReply>;
export const ChatListReply = z.object({
  chats: z.array(Conversation),
  groups: z.array(Conversation),
  requests: z.array(Conversation),
  blockedUserIds: z.array(z.string().uuid()),
});
export type ChatListReply = z.infer<typeof ChatListReply>;
export const ChatMessagesReply = z.object({ messages: z.array(ChatMessage) });
export type ChatMessagesReply = z.infer<typeof ChatMessagesReply>;
export const ChatSend = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('text'),
      text: z.string().trim().min(1).max(CHAT_TEXT_MAX),
      replyTo: z.string().uuid().nullable().optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('sticker'),
      sticker: z.enum(STICKER_IDS as [StickerId, ...StickerId[]]),
      replyTo: z.string().uuid().nullable().optional(),
    })
    .strict(),
]);
export type ChatSend = z.infer<typeof ChatSend>;
export const ChatEdit = z.object({ text: z.string().trim().min(1).max(CHAT_TEXT_MAX) }).strict();
export type ChatEdit = z.infer<typeof ChatEdit>;
export const ChatReport = z.object({ reason: z.string().trim().min(3).max(300) }).strict();
export type ChatReport = z.infer<typeof ChatReport>;
export const VoiceAllow = z.object({ allow: z.boolean() }).strict();
export type VoiceAllow = z.infer<typeof VoiceAllow>;
export const ChatReact = z
  .object({ emoji: z.enum(CHAT_REACTIONS as unknown as [ChatReactionEmoji, ...ChatReactionEmoji[]]).nullable() })
  .strict();
export type ChatReact = z.infer<typeof ChatReact>;
export const ChatForward = z
  .object({ conversationIds: z.array(z.string().uuid()).min(1).max(FORWARD_MAX) })
  .strict()
  .refine((value) => new Set(value.conversationIds).size === value.conversationIds.length, 'Each chat once');
export type ChatForward = z.infer<typeof ChatForward>;
export const ChatMessageReply = z.object({ message: ChatMessage });
export type ChatMessageReply = z.infer<typeof ChatMessageReply>;
export const ChatForwardReply = z.object({ sent: z.number().int(), message: z.string() });
export type ChatForwardReply = z.infer<typeof ChatForwardReply>;

const GroupName = z.string().trim().min(1, 'Give the group a name').max(GROUP_NAME_MAX);
export const GroupCreate = z
  .object({
    name: GroupName,
    icon: GroupIconSchema.default('👥'),
    lifeIds: z
      .array(z.string().uuid())
      .min(1)
      .max(GROUP_MAX_MEMBERS - 1),
  })
  .strict();
export type GroupCreate = z.infer<typeof GroupCreate>;
export const GroupAction = z.discriminatedUnion('action', [
  z.object({ action: z.literal('rename'), name: GroupName, icon: GroupIconSchema.optional() }).strict(),
  z
    .object({
      action: z.literal('add'),
      lifeIds: z
        .array(z.string().uuid())
        .min(1)
        .max(GROUP_MAX_MEMBERS - 1),
    })
    .strict(),
  z.object({ action: z.literal('remove'), userId: z.string().uuid() }).strict(),
  z.object({ action: z.literal('make_admin'), userId: z.string().uuid() }).strict(),
  z.object({ action: z.literal('leave') }).strict(),
]);
export type GroupAction = z.infer<typeof GroupAction>;
export const GroupReply = z.object({ conversation: Conversation, message: z.string() });
export type GroupReply = z.infer<typeof GroupReply>;

// ---- moderators: reported messages
export const ChatReportView = z.object({
  id: z.string().uuid(),
  reason: z.string(),
  createdAt: z.string(),
  reporter: z.object({ userId: z.string().uuid(), name: z.string() }),
  message: z.object({
    id: z.string().uuid(),
    kind: MessageKind,
    text: z.string().nullable(),
    deletedAt: z.string().nullable(),
    createdAt: z.string(),
    conversationKind: ChatKind,
  }),
  sender: z.object({ userId: z.string().uuid(), name: z.string() }),
  // the messages around it, for context
  context: z.array(
    z.object({ sender: z.string(), text: z.string().nullable(), createdAt: z.string(), reported: z.boolean() }),
  ),
  resolvedAt: z.string().nullable(),
  resolvedBy: z.string().nullable(),
  outcome: z.string().nullable(),
});
export type ChatReportView = z.infer<typeof ChatReportView>;
export const ChatReportsReply = z.object({ reports: z.array(ChatReportView), open: z.number().int() });
export type ChatReportsReply = z.infer<typeof ChatReportsReply>;
export const ChatReportDecision = z
  .object({ outcome: z.enum(['dismissed', 'removed']), note: z.string().trim().max(300).optional() })
  .strict();
export type ChatReportDecision = z.infer<typeof ChatReportDecision>;

// ---- live events over the WebSocket (the server sends; the client only subscribes)
export const ChatEvent = z.discriminatedUnion('type', [
  // request: the person it reached has not chatted with the sender (a quiet notice, no pop-up or badge)
  z.object({
    type: z.literal('message'),
    message: ChatMessage,
    request: z.boolean().optional(),
    // a group's message carries the group's name (for the pop-up)
    group: z.object({ name: z.string() }).optional(),
  }),
  z.object({ type: z.literal('edited'), message: ChatMessage }),
  z.object({ type: z.literal('deleted'), conversationId: z.string().uuid(), id: z.string().uuid() }),
  z.object({ type: z.literal('read'), conversationId: z.string().uuid(), userId: z.string().uuid(), at: z.string() }),
  z.object({
    type: z.literal('reacted'),
    conversationId: z.string().uuid(),
    id: z.string().uuid(),
    reactions: z.array(ChatReaction),
  }),
  // a group changed (name, members) or I was added to or removed from one
  z.object({ type: z.literal('group'), conversationId: z.string().uuid() }),
  // the London Life team posted (or a poll's numbers moved): read the team chat again
  z.object({ type: z.literal('team') }),
  // something new under the bell
  z.object({ type: z.literal('notice') }),
  // a visit changed: someone knocked, was let in or left, or the host answered
  z.object({ type: z.literal('visit') }),
  // a seat on a flight was offered to me, or someone answered the seats I offered: look at flights again
  z.object({ type: z.literal('flight') }),
  // the admin published new content (places, activities): the game loads it again
  z.object({ type: z.literal('content'), version: z.number().int() }),
]);
export type ChatEvent = z.infer<typeof ChatEvent>;
export const ClientEvent = z.discriminatedUnion('type', [
  z.object({ type: z.literal('subscribe'), conversationId: z.string().uuid() }),
  z.object({ type: z.literal('unsubscribe'), conversationId: z.string().uuid() }),
  z.object({ type: z.literal('ping') }),
]);
export type ClientEvent = z.infer<typeof ClientEvent>;
